

import React, { useState } from 'react';
import {
  Modal,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from 'assets/theme/base/colors';

const NewGuideOnboardingModal = ({ isOpen, onClose, onSave }) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    is_available: false,
    comments: '',
    type: 'Tour Guide',
    operating_country: '',
    tax_id: '',
    tax_rate: '',
    is_onboarding_complete: false,
    application_status: 'In Review',
    secondary_email: '',
    contract_url: '',
    license_url: '',
    payment_type: '',
    government_id_url: '',
    payment_amount: '',
    first_state_certificate_url: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = 'Full Name is required';
    if (!formData.country) errors.country = 'Country is required';
    if (!formData.phone) errors.phone = 'Phone is required';
    if (!formData.email) errors.email = 'Email is required';
    if (!formData.address) errors.address = 'Address is required';
    if (!formData.city) errors.city = 'City is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : null,
        payment_amount: formData.payment_amount
          ? parseFloat(formData.payment_amount)
          : null,
        created_by: user?.id,
        updated_by: user?.id,
      };

      await onSave(payload);
      onClose();
      setFormData({
        name: '',
        country: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        state: '',
        is_available: false,
        comments: '',
        type: 'Tour Guide',
        operating_country: '',
        tax_id: '',
        tax_rate: '',
        is_onboarding_complete: false,
        application_status: 'In Review',
        secondary_email: '',
        contract_url: '',
        license_url: '',
        payment_type: '',
        government_id_url: '',
        payment_amount: '',
        first_state_certificate_url: '',
      });
    } catch (error) {
      console.error('Error saving Guide Onboarding:', error);
      setToast({
        open: true,
        message:
          error.response?.data?.detail || 'Error saving Guide Onboarding',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: '800px' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <MDTypography variant="h6" color="white">
              Add New Guide Onboarding
            </MDTypography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 4, overflowY: 'auto' }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    error={!!formErrors.country}
                    helperText={formErrors.country}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    error={!!formErrors.address}
                    helperText={formErrors.address}
                    required
                    multiline
                    minRows={2}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    error={!!formErrors.city}
                    helperText={formErrors.city}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDBox mt={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_available}
                          onChange={handleInputChange}
                          name="is_available"
                        />
                      }
                      label="Available"
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Comments"
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    multiline
                    minRows={2}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      label="Type"

                    >
                      <MenuItem value="DMC">DMC</MenuItem>
                      <MenuItem value="Freelance">Freelance</MenuItem>
                      <MenuItem value="Tour Guide">Tour Guide</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Operating Country"
                    name="operating_country"
                    value={formData.operating_country}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tax ID"
                    name="tax_id"
                    value={formData.tax_id}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tax Rate (%)"
                    name="tax_rate"
                    type="number"
                    value={formData.tax_rate}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDBox mt={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_onboarding_complete}
                          onChange={handleInputChange}
                          name="is_onboarding_complete"
                        />
                      }
                      label="Onboarding Complete"
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Application Status</InputLabel>
                    <Select
                      name="application_status"
                      value={formData.application_status}
                      onChange={handleInputChange}
                      label="Application Status"

                    >
                      <MenuItem value="In Review">In Review</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Declined">Declined</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Secondary Email"
                    name="secondary_email"
                    type="email"
                    value={formData.secondary_email}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contract URL"
                    name="contract_url"
                    value={formData.contract_url}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="License URL"
                    name="license_url"
                    value={formData.license_url}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Payment Type</InputLabel>
                    <Select
                      name="payment_type"
                      value={formData.payment_type}
                      onChange={handleInputChange}
                      label="Payment Type"

                    >
                      <MenuItem value="Per day">Per day</MenuItem>
                      <MenuItem value="Per trip">Per trip</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Government ID URL"
                    name="government_id_url"
                    value={formData.government_id_url}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    name="payment_amount"
                    type="number"
                    value={formData.payment_amount}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First State Certificate URL"
                    name="first_state_certificate_url"
                    value={formData.first_state_certificate_url}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewGuideOnboardingModal;


